<template>
  <b-modal
    :visible="show"
    @hide="$emit('close')"
    title="Verificação"
    centered
    hide-footer
    no-close-on-backdrop
  >
    <div class="text-center">
      <p class="mb-2">Digite o código enviado ao e-mail do titular</p>

      <!-- TIMER -->
      <p class="mb-3">
        Código expira em:
        <strong :class="timerExpired ? 'text-danger' : 'text-primary'">
          {{ formattedTimer }}
        </strong>
      </p>

      <!-- INPUTS -->
      <div class="d-flex justify-content-center mb-3">
        <input
          v-for="(digit, index) in code"
          :key="index"
          ref="inputs"
          type="tel"
          inputmode="numeric"
          pattern="[0-9]*"
          class="form-control text-center mx-1"
          style="width: 45px; font-size: 20px"
          :value="code[index]"
          @input="handleInput($event, index)"
          @keydown.backspace="handleBackspace(index)"
          @paste.prevent="handlePaste($event, index)"
        />
      </div>

      <!-- BOTÃO -->
      <b-button
        variant="primary"
        class="w-100"
        :disabled="isLoading || code.join('').length !== 6 || timerExpired"
        @click="verifyCode"
      >
        <b-spinner small v-if="isLoading" class="mr-2"></b-spinner>
        Verificar
      </b-button>

      <!-- ERRO -->
      <p v-if="error" class="text-danger mt-2">
        {{ error }}
      </p>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: 'VerificationCodeModal',

  props: {
    show: Boolean,
  },

  data() {
    return {
      code: ['', '', '', '', '', ''],
      isLoading: false,
      error: null,

      timer: 120,
      timerInterval: null,
    };
  },

  computed: {
    formattedTimer() {
      const minutes = String(Math.floor(this.timer / 60)).padStart(2, '0');
      const seconds = String(this.timer % 60).padStart(2, '0');
      return `${minutes}:${seconds}`;
    },

    timerExpired() {
      return this.timer <= 0;
    },
  },

  watch: {
    show(value) {
      if (value) {
        this.startTimer();
        this.$nextTick(() => {
          this.$refs.inputs?.[0]?.focus();
        });
      } else {
        this.stopTimer();
        this.reset();
      }
    },
  },

  methods: {
    startTimer() {
      this.stopTimer();
      this.timer = 120;

      this.timerInterval = setInterval(() => {
        if (this.timer <= 0) {
          this.stopTimer();
          return;
        }

        this.timer -= 1;
      }, 1000);
    },

    stopTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    },

    reset() {
      this.code = ['', '', '', '', '', ''];
      this.error = null;
      this.isLoading = false;
    },

    handleInput(event, index) {
      const raw = event.target.value.replace(/\D/g, '');

      if (raw.length > 1) {
        const digits = raw.slice(0, 6 - index);
        digits.split('').forEach((digit, i) => {
          const target = index + i;
          if (target < 6) {
            this.$set(this.code, target, digit);
            if (this.$refs.inputs[target]) {
              this.$refs.inputs[target].value = digit;
            }
          }
        });

        const nextFocus = Math.min(index + digits.length, 5);
        this.$nextTick(() => {
          this.$refs.inputs[nextFocus].focus();
        });
        return;
      }

      const value = raw.slice(-1);
      this.$set(this.code, index, value);
      event.target.value = value;

      if (value && index < 5) {
        this.$nextTick(() => {
          this.$refs.inputs[index + 1].focus();
        });
      }
    },

    handlePaste(event, index) {
      const pasted = (event.clipboardData || window.clipboardData)
        .getData('text')
        .replace(/\D/g, '')
        .slice(0, 6);

      if (!pasted) return;

      pasted.split('').forEach((digit, i) => {
        const target = index + i;
        if (target < 6) {
          this.$set(this.code, target, digit);
          if (this.$refs.inputs[target]) {
            this.$refs.inputs[target].value = digit;
          }
        }
      });

      const nextFocus = Math.min(index + pasted.length, 5);
      this.$nextTick(() => {
        this.$refs.inputs[nextFocus].focus();
      });
    },

    handleBackspace(index) {
      if (!this.code[index] && index > 0) {
        this.$nextTick(() => {
          this.$refs.inputs[index - 1].focus();
        });
      }
    },

    verifyCode() {
      if (this.timerExpired) {
        this.error = 'Código expirado. Solicite um novo código.';
        return;
      }

      this.isLoading = true;
      this.error = null;

      const codeString = this.code.join('');

      this.$emit('verify', {
        codigoVerificacao: codeString,
        onSuccess: () => {
          this.stopTimer();
          this.reset();
          this.$emit('success', codeString);
        },
        onError: (msg) => {
          this.error = msg || 'Código inválido';
        },
        onFinish: () => {
          this.isLoading = false;
        },
      });
    },
  },
};
</script>
